import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Switch } from "@/components/ui/switch";
import { useQuery } from "@tanstack/react-query";
import { Plus, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader } from "@/components/AppShell";
import { PathTrail } from "@/components/PathTrail";
import { ItemCard, ItemThumb, isLow, LowBadge } from "@/components/ItemCard";
import { CategorySelect } from "@/components/Selects";
import { api } from "@/lib/api";
import { NONE, useCategories, useCatLabel } from "@/lib/hooks";
import { useWs } from "@/context/Auth";
import { useI18n } from "@/i18n/I18n";

export default function Items() {
  const { t, fmtNum, fmtDate } = useI18n();
  const navigate = useNavigate();
  const { canWrite } = useWs();
  const [q, setQ] = useState("");
  const [cat, setCat] = useState(null);
  const [params] = useSearchParams();
  const [low, setLow] = useState(params.get("low") === "1");
  const { data: cats = NONE } = useCategories();
  const catLabel = useCatLabel();
  const catById = useMemo(() => Object.fromEntries(cats.map((c) => [c.id, c])), [cats]);
  const { data: items = [], isLoading } = useQuery({
    queryKey: ["items", q, cat || "", low],
    queryFn: () => api.get("/items", { params: { q, category_id: cat || "", low } }).then((r) => r.data),
    placeholderData: (prev) => prev,
  });

  return (
    <div>
      <PageHeader title={t("item.title")} subtitle={`${fmtNum(items.length)}`} actions={canWrite && (
        <Button data-testid="items-add-button" asChild className="h-11 gap-2"><Link to="/items/new"><Plus className="w-4 h-4" />{t("item.add")}</Link></Button>)} />
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input data-testid="items-filter-input" className="h-12 ps-10" placeholder={t("search.placeholder")} value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <label className="flex items-center gap-2 text-sm font-medium whitespace-nowrap"><Switch data-testid="items-low-filter" checked={low} onCheckedChange={setLow} />{t("stock.filter")}</label>
        <div className="sm:w-60"><CategorySelect value={cat} onChange={setCat} cats={cats} label={catLabel} testId="items-category-filter" /></div>
      </div>
      {isLoading ? <p className="text-slate-500">{t("common.loading")}</p> : !items.length ? (
        <p data-testid="items-empty" className="rounded-xl border border-dashed border-slate-300 p-10 text-center text-slate-500">{t("item.empty")}</p>
      ) : (
        <>
          <div className="grid gap-3 md:hidden">{items.map((it) => <ItemCard key={it.id} item={it} catLabel={catLabel(catById[it.category_id])} />)}</div>
          <div className="hidden md:block rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="items-table">
            <Table>
              <TableHeader><TableRow>
                <TableHead>{t("common.name")}</TableHead><TableHead>{t("common.category")}</TableHead>
                <TableHead className="text-end">{t("common.quantity")}</TableHead><TableHead>{t("common.location")}</TableHead><TableHead />
              </TableRow></TableHeader>
              <TableBody>
                {items.map((it) => (
                  <TableRow key={it.id} data-testid={`items-row-${it.id}`} className="cursor-pointer" onClick={() => navigate(`/items/${it.id}`)}>
                    <TableCell><span className="flex items-center gap-3"><ItemThumb item={it} className="w-10 h-10" /><span className="font-semibold">{it.name}</span>{isLow(it) && <LowBadge testId={`items-row-low-${it.id}`} />}</span></TableCell>
                    <TableCell className="text-slate-600">{catLabel(catById[it.category_id])}</TableCell>
                    <TableCell className="text-end font-mono">{fmtNum(it.quantity)}</TableCell>
                    <TableCell><PathTrail path={it.path} emptyLabel={t("common.noLocation")} testId={`items-row-path-${it.id}`} /></TableCell>
                    <TableCell className="text-xs text-slate-400 whitespace-nowrap">{fmtDate(it.updated_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </>
      )}
    </div>
  );
}
